import {
  Home,
  BookCheck,
  ChartColumnDecreasing,
  CalendarDays,
  ClipboardCheck,
  BookOpen,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';

type StudentMenuItem = {
  to: string;
  icon: LucideIcon;
  /** Chỉ active đúng route này, không kéo theo route con. */
  end?: boolean;
  /** Active theo route cha khi link trỏ tới một route con. */
  activePath?: string;
  hiddenForK12?: boolean;
  labelKey?: string;
  label?: string | ((isK12Student: boolean) => string);
};

const STUDENT_MENU: StudentMenuItem[] = [
  {
    to: '/student/dashboard',
    icon: Home,
    labelKey: 'rolemenu.student.home',
    end: true,
  },
  {
    to: '/student/calendar',
    icon: CalendarDays,
    labelKey: 'rolemenu.student.calendar',
    hiddenForK12: true,
  },
  {
    to: '/student/book',
    icon: BookOpen,
    label: (isK12Student) =>
      isK12Student ? 'Tài liệu thực hành' : 'Sách & Tài liệu thực hành',
  },
  {
    to: '/student/practice',
    icon: BookCheck,
    label: 'Bài tập',
  },
  {
    to: '/student/examtest/overview',
    activePath: '/student/examtest',
    icon: ClipboardCheck,
    label: 'Kiểm tra',
  },
  {
    to: '/student/report',
    icon: ChartColumnDecreasing,
    labelKey: 'rolemenu.student.report',
  },
];

const itemClassName = (active: boolean) =>
  [
    'student-menu-link flex w-full cursor-pointer select-none items-center font-bold h-[40px] rounded-lg pl-3',
    active ? 'active-menu-item' : '',
  ].join(' ');

function isMenuItemActive(item: StudentMenuItem, pathname: string) {
  if (item.end) return pathname === item.to;
  const base = item.activePath ?? item.to;
  return pathname === base || pathname.startsWith(`${base}/`);
}

const StudentMenuLink = ({
  item,
  label,
  active,
  onSelect,
}: {
  item: StudentMenuItem;
  label: string;
  active: boolean;
  onSelect: (to: string) => void;
}) => {
  const Icon = item.icon;

  return (
    <li className="mb-2">
      <NavLink
        to={item.to}
        end={item.end}
        className={itemClassName(active)}
        onClick={() => onSelect(item.to)}
      >
        <Icon size={23} className="text-primary mr-2" />
        <span>{label}</span>
      </NavLink>
    </li>
  );
};

const Sidebar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { auth, isStudent } = useAuth();
  const isK12Student = isStudent && Boolean(auth?.k12_username);
  const [pendingTo, setPendingTo] = useState<string | null>(null);

  useEffect(() => {
    setPendingTo(null);
  }, [pathname]);

  const menuItems = STUDENT_MENU.filter((item) => !(item.hiddenForK12 && isK12Student));

  return (
    <div className="w-64 h-screen bg-white p-4 text-gray3 font-bold text-[15px]">
      <nav>
        <ul>
          {menuItems.map((item) => {
            const label = item.labelKey
              ? t(item.labelKey)
              : typeof item.label === 'function'
                ? item.label(isK12Student)
                : item.label ?? '';

            const active = pendingTo
              ? pendingTo === item.to
              : isMenuItemActive(item, pathname);

            return (
              <StudentMenuLink
                key={item.to}
                item={item}
                label={label}
                active={active}
                onSelect={setPendingTo}
              />
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
